/**
 * ticketTimingUtils.js — Item-level fire time model for KDS timing.
 *
 * Industry standard (based on Toast KDS):
 *   - Ticket time = time since order was SENT (createdAt) — informational only
 *   - Fire time   = time since an item became ACTIVE on the KDS — per-item
 *
 * When new items are added to a completed ticket:
 *   - Original items keep their original fire time (createdAt)
 *   - New items get fire time = when they were detected (reopenedAt)
 *   - The card timer shows the oldest UNFULFILLED item's fire time
 *   - Alerts only trigger based on individual item fire times
 *
 * All functions are pure — no React, no Firestore, fully testable.
 */

/**
 * Whether an item was added/updated AFTER the order was originally placed.
 *
 * Original items are stamped with the order's createdAt, so a fire time that is
 * meaningfully later than createdAt marks a later addition. Used to show the
 * per-item time badge only on the items that actually changed, instead of
 * repeating the order age on every original line.
 *
 * @param {Object} params
 * @param {number|null} params.fireMs    — the item's fire time (ms)
 * @param {number|null} params.createdMs — the order's creation time (ms)
 * @param {number} [params.graceMs]      — how much later than createdAt counts as "added later"
 * @returns {boolean}
 */
export function isLaterAddedItem({ fireMs, createdMs, graceMs = 60 * 1000 }) {
  if (fireMs == null || createdMs == null) return false;
  return fireMs - createdMs > graceMs;
}

/**
 * Stamp fire times for items that have newly appeared on an ACTIVE ticket.
 *
 * Square gives us no per-item "added at" timestamp, so the only way to know an
 * item is new is to remember which item indices we've already recorded. This
 * function preserves every already-stamped index and assigns a fire time to any
 * index that doesn't have one yet:
 *   - First stamp ever (nothing stored) → those items are the original order, so
 *     they get `createdAtMs`.
 *   - Later stamps (some indices already stored) → the missing indices are items
 *     added after the order was placed, so they get `nowMs`.
 *
 * Returns the merged map, or null when nothing changed (so callers can skip the
 * Firestore write).
 *
 * @param {Object} params
 * @param {Object|null} params.storedFireTimes — already-persisted { [idx]: ms }
 * @param {number} params.itemCount            — current total item count
 * @param {number} params.createdAtMs          — order creation timestamp (ms)
 * @param {number} params.nowMs                — current time (ms)
 * @returns {Object|null} merged fire times, or null if no new indices
 */
export function computeStampedFireTimes({ storedFireTimes, itemCount, createdAtMs, nowMs }) {
  const stored = storedFireTimes && typeof storedFireTimes === 'object' ? storedFireTimes : null;
  const isFirstStamp = !stored || Object.keys(stored).length === 0;
  const next = { ...(stored || {}) };
  let changed = false;
  for (let i = 0; i < itemCount; i++) {
    if (next[i] != null) continue;
    next[i] = isFirstStamp ? createdAtMs : nowMs;
    changed = true;
  }
  return changed ? next : null;
}

/**
 * Decide whether an order should trigger a "new order" alert.
 *
 * Guards against the two ways a non-new order can sneak through:
 *   1. Backlog on a cold open — an order created BEFORE this session started
 *      must never alert, or opening the board mid-service blasts a chime/push
 *      for every order already in progress.
 *   2. An old order resurfacing in a poll — anything outside the freshness
 *      window is ignored.
 *
 * A small `graceMs` absorbs clock skew between the Square server (createdAt)
 * and the local clock (mountMs/nowMs) so a genuinely just-placed order isn't
 * suppressed by a couple of seconds of drift.
 *
 * @param {Object} order               — order with a `createdAt` (ISO string)
 * @param {Object} params
 * @param {number} params.mountMs       — when the alert hook mounted (session start)
 * @param {number} params.nowMs         — current time (ms)
 * @param {number} [params.freshWindowMs] — max age to still count as "new"
 * @param {number} [params.graceMs]       — clock-skew tolerance around mountMs
 * @returns {boolean}
 */
export function isFreshNewOrder(order, { mountMs, nowMs, freshWindowMs = 20 * 60 * 1000, graceMs = 60 * 1000 }) {
  const createdMs = order && order.createdAt ? new Date(order.createdAt).getTime() : nowMs;
  if (!Number.isFinite(createdMs)) return false;
  if (nowMs - createdMs > freshWindowMs) return false;   // too old (resurfaced poll)
  if (createdMs < mountMs - graceMs) return false;        // existed before this session
  return true;
}

/**
 * Derive how many items existed when a ticket was FIRST completed.
 *
 * This is the "snapshot count" used to tell original items apart from items
 * added after a completion. It MUST be 0 for any ticket that has never been
 * completed — otherwise still-unchecked original items get misclassified as
 * "new" and their fire time gets reset mid-service.
 *
 * Rules (in priority order):
 *   1. A real completion snapshot exists → use its length. This is the only
 *      authoritative source and is written by sqFsComplete.
 *   2. No snapshot, but the ticket was manually completed (old data / legacy
 *      records) → fall back to the number of checked items at completion.
 *   3. Otherwise the ticket is still in progress → 0. Checking items during
 *      normal service must NEVER bump this number.
 *
 * @param {Object} params
 * @param {Array|null} params.itemsSnapshot     — items captured at first completion
 * @param {boolean} params.manuallyCompleted    — ticket was completed via the Complete button
 * @param {number[]|null} params.checkedItems   — currently checked item indices
 * @returns {number} snapshot count (0 = never completed)
 */
export function deriveSnapshotCount({ itemsSnapshot, manuallyCompleted, checkedItems }) {
  if (Array.isArray(itemsSnapshot)) return itemsSnapshot.length;
  if (manuallyCompleted && Array.isArray(checkedItems)) return checkedItems.length;
  return 0;
}

/**
 * Build the itemFireTimes map for a ticket.
 *
 * @param {Object} params
 * @param {number} params.itemCount       — total items on the ticket now
 * @param {number} params.createdAtMs     — original order creation timestamp (ms)
 * @param {number} params.snapshotCount   — how many items existed when first completed (0 = never completed)
 * @param {Object|null} params.existingFireTimes — already-stored { [idx]: ms } from Firestore
 * @param {number|null} params.reopenedAtMs      — when new items were detected (ms)
 * @param {Object|null} params.itemCheckedMs     — { [idx]: ms } per-item check timestamps (fallback)
 * @returns {Object} { [idx]: fireTimeMs }
 */
export function buildItemFireTimes({
  itemCount,
  createdAtMs,
  snapshotCount = 0,
  existingFireTimes = null,
  reopenedAtMs = null,
  itemCheckedMs = null,
}) {
  const result = {};
  const existing = existingFireTimes || {};

  // Determine fallback fire time for new items when reopenedAt is missing:
  // use the latest check time of original items (= when the first cycle ended)
  let newItemFallback = reopenedAtMs || createdAtMs;
  if (!reopenedAtMs && snapshotCount > 0 && itemCheckedMs) {
    const origCheckTimes = Object.entries(itemCheckedMs)
      .filter(([k]) => parseInt(k) < snapshotCount)
      .map(([, v]) => v)
      .filter(v => typeof v === 'number');
    if (origCheckTimes.length > 0) {
      newItemFallback = Math.max(...origCheckTimes);
    }
  }

  for (let i = 0; i < itemCount; i++) {
    if (existing[i] != null) {
      // Preserve already-recorded fire time
      result[i] = existing[i];
    } else if (snapshotCount > 0 && i >= snapshotCount) {
      // New item added after first completion
      result[i] = newItemFallback;
    } else {
      // Original item — fire time = order creation
      result[i] = createdAtMs;
    }
  }

  return result;
}

/**
 * Compute the active elapsed time for a ticket's header display.
 *
 * - If there are unfulfilled items: time since the OLDEST unfulfilled item was fired.
 * - If ALL items are fulfilled (but ticket not yet completed): max of per-item durations
 *   so staff sees how long the work actually took while waiting to hit "Complete".
 *
 * @param {Object} params
 * @param {Object} params.itemFireTimes   — { [idx]: fireTimeMs }
 * @param {number[]} params.checkedItems  — indices of fulfilled items
 * @param {Object} [params.itemCheckedMs] — { [idx]: checkedAtMs }
 * @param {number} params.nowMs           — current time in ms
 * @returns {number} elapsed minutes
 */
export function computeActiveElapsed({ itemFireTimes, checkedItems, itemCheckedMs, nowMs }) {
  const fires = itemFireTimes || {};
  const checked = new Set(checkedItems || []);
  const entries = Object.entries(fires);

  const unfulfilled = entries
    .filter(([idx]) => !checked.has(parseInt(idx)))
    .map(([, ms]) => ms);

  if (unfulfilled.length > 0) {
    // Active items exist — show time since oldest unfulfilled item was fired
    const oldestFireTime = Math.min(...unfulfilled);
    return Math.round((nowMs - oldestFireTime) / 60000);
  }

  // All items checked but ticket not yet marked complete —
  // show max of per-item durations (how long the longest item waited)
  if (itemCheckedMs && entries.length > 0) {
    let maxDur = 0;
    for (const [idx, fireMs] of entries) {
      const checkMs = itemCheckedMs[idx];
      if (checkMs != null && fireMs != null) {
        maxDur = Math.max(maxDur, Math.round((checkMs - fireMs) / 60000));
      }
    }
    if (maxDur > 0) return maxDur;
  }

  return 0;
}

/**
 * Compute per-item duration (from fire time to check time).
 *
 * @param {Object} params
 * @param {number} params.itemIndex
 * @param {Object} params.itemFireTimes   — { [idx]: fireTimeMs }
 * @param {Object} params.itemCheckedMs   — { [idx]: checkedAtMs }
 * @param {number} [params.createdAtMs]   — fallback if no fire time recorded
 * @returns {number|null} minutes, or null if not yet checked
 */
export function computeItemDuration({ itemIndex, itemFireTimes, itemCheckedMs, createdAtMs }) {
  const checkedMs = itemCheckedMs?.[itemIndex];
  if (checkedMs == null) return null;

  const fireMs = itemFireTimes?.[itemIndex] ?? createdAtMs ?? null;
  if (fireMs == null) return null;

  return Math.max(0, Math.round((checkedMs - fireMs) / 60000));
}

/**
 * Compute the elapsed time to display on a COMPLETED ticket's header.
 * For reopened tickets, shows the duration of the latest active cycle
 * (newest fire time to latest check), not the inflated timeTakenMin.
 *
 * @param {Object} params
 * @param {number|null} params.timeTakenMin  — stored value from Firestore
 * @param {number} params.snapshotCount      — original item count at first completion
 * @param {number} params.itemCount          — current total items
 * @param {Object|null} params.itemFireTimes — { [idx]: fireTimeMs }
 * @param {Object|null} params.itemCheckedMs — { [idx]: checkedAtMs }
 * @returns {number|null} corrected elapsed minutes
 */
export function computeCompletedElapsed({ timeTakenMin, snapshotCount, itemCount, itemFireTimes, itemCheckedMs, createdAtMs }) {
  // If we have fire times and check times, compute max of per-item durations.
  // This is the industry standard: "Time Taken" = longest any item waited.
  if (itemFireTimes && itemCheckedMs) {
    const durations = [];
    const count = itemCount || Math.max(
      ...Object.keys(itemFireTimes).map(k => parseInt(k) + 1),
      ...Object.keys(itemCheckedMs).map(k => parseInt(k) + 1),
      0
    );
    for (let i = 0; i < count; i++) {
      const fireMs = itemFireTimes[i] ?? createdAtMs ?? null;
      const checkMs = itemCheckedMs[i];
      if (fireMs != null && checkMs != null) {
        durations.push(Math.max(0, Math.round((checkMs - fireMs) / 60000)));
      }
    }
    if (durations.length > 0) return Math.max(...durations);
  }

  // No fire times available — use stored value
  return timeTakenMin;
}

/**
 * Determine which item indices are delayed (past threshold).
 *
 * @param {Object} params
 * @param {number} params.itemCount
 * @param {Object} params.itemFireTimes   — { [idx]: fireTimeMs }
 * @param {number[]} params.checkedItems  — fulfilled indices
 * @param {number} params.nowMs
 * @param {number} params.thresholdMin    — delay threshold in minutes
 * @returns {number[]} array of delayed item indices
 */
export function computeDelayedItems({ itemCount, itemFireTimes, checkedItems, nowMs, thresholdMin }) {
  const checked = new Set(checkedItems || []);
  const delayed = [];
  for (let i = 0; i < itemCount; i++) {
    if (checked.has(i)) continue;
    const fireMs = itemFireTimes?.[i];
    if (fireMs == null) continue;
    const elapsedMin = (nowMs - fireMs) / 60000;
    if (elapsedMin >= thresholdMin) delayed.push(i);
  }
  return delayed;
}
